import React, { useState, useEffect, useRef, useContext } from "react";
import io from "socket.io-client";
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout";
import SpeedMeetingGroupsTable from "./components/GroupTables";
import { UserContext } from "../../context/UserContext";
import { Box, Typography, TextField, Button } from "@mui/material";
import { useProfileComplete } from "../../hooks/useProfileComplete";
let socketServer = process.env.REACT_APP_MKGF_MASTODON_API

// socket.io-client treats any path in the connection URL as a namespace to
// join, not a route (e.g. ".../api" tries to join namespace "/api"). This env
// var carries a "/api" suffix for REST calls elsewhere, so strip it down to
// just the origin here or the server rejects the namespace and immediately
// closes the connection.
const socket = io(new URL(socketServer).origin); // Connect to backend server

const SpeedMeeting = () => {
    const [name, setName] = useState("");
    const [group, setGroup] = useState("");
    const [roomJoined, setRoomJoined] = useState(false);
    const [pairedUser, setPairedUser] = useState(null);
    const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
    const [sessionInfo, setSessionInfo] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [totalUsers, setTotalUsers] = useState(0);
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const peerConnection = useRef(null);
    
    const { userDetails } = useContext(UserContext)
    
    // Check profile completion and redirect if needed
    useProfileComplete(userDetails)
  
    useEffect(() => {
      if (roomJoined && pairedUser) {
        setTimeLeft(300); // Reset to 5 minutes when paired
        const timer = setInterval(() => {
          setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
  
        return () => clearInterval(timer);
      }
    }, [roomJoined, pairedUser]);
  
    useEffect(() => {
      // Add connection event listeners for debugging
      socket.on("connect", () => {
        console.log("Socket connected with ID:", socket.id);
      });

      socket.on("disconnect", () => {
        console.log("Socket disconnected");
      });

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
      });

      // Socket event listeners
      socket.on("joined-room", ({ group, totalUsers, message }) => {
        console.log("Joined room event received:", { group, totalUsers, message });
        setTotalUsers(totalUsers);
        setStatusMessage(message);
        console.log(`Joined room ${group} with ${totalUsers} users`);
      });

      socket.on("user-joined", ({ user, totalUsers }) => {
        setTotalUsers(totalUsers);
        setStatusMessage(`${user.name} joined the session. Total participants: ${totalUsers}`);
      });

      socket.on("user-left", ({ user, totalUsers }) => {
        setTotalUsers(totalUsers);
        setStatusMessage(`${user.name} left the session. Total participants: ${totalUsers}`);
      });

      socket.on("paired", ({ partner }) => {
        console.log("Paired with:", partner);
        setPairedUser(partner);
        setStatusMessage(`You are now paired with ${partner.name}`);
        setTimeLeft(300); // Reset timer to 5 minutes
        initiateWebRTC(partner.id);
      });

      socket.on("session-ending", ({ message }) => {
        setStatusMessage(message);
      });

      socket.on("session-complete", ({ message }) => {
        setStatusMessage(message);
        setPairedUser(null);
        if (peerConnection.current) {
          peerConnection.current.close();
          peerConnection.current = null;
        }
      });

      socket.on("session-paused", ({ message }) => {
        setStatusMessage(message);
        setPairedUser(null);
        if (peerConnection.current) {
          peerConnection.current.close();
          peerConnection.current = null;
        }
      });

      socket.on("session-info", (info) => {
        setSessionInfo(info);
      });

      // WebRTC event listeners with improved error handling
      socket.on("offer", async ({ offer, from }) => {
        if (peerConnection.current) {
          try {
            await peerConnection.current.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await peerConnection.current.createAnswer();
            await peerConnection.current.setLocalDescription(answer);
            socket.emit("answer", { answer, partnerId: from });
          } catch (error) {
            console.error("Error handling offer:", error);
          }
        }
      });

      socket.on("answer", async ({ answer, from }) => {
        if (peerConnection.current) {
          try {
            await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
          } catch (error) {
            console.error("Error handling answer:", error);
          }
        }
      });

      socket.on("ice-candidate", ({ candidate, from }) => {
        if (peerConnection.current) {
          try {
            peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (error) {
            console.error("Error adding ICE candidate:", error);
          }
        }
      });

      // Cleanup function
      return () => {
        socket.off("joined-room");
        socket.off("user-joined");
        socket.off("user-left");
        socket.off("paired");
        socket.off("session-ending");
        socket.off("session-complete");
        socket.off("session-paused");
        socket.off("session-info");
        socket.off("offer");
        socket.off("answer");
        socket.off("ice-candidate");
      };
    }, []);
  
    const initiateWebRTC = async (partnerId) => {
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
        }

        peerConnection.current = new RTCPeerConnection({
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" }
          ]
        });

        localStream.getTracks().forEach((track) => {
          peerConnection.current.addTrack(track, localStream);
        });

        peerConnection.current.ontrack = (event) => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        };

        peerConnection.current.onicecandidate = (event) => {
          if (event.candidate) {
            socket.emit("ice-candidate", { candidate: event.candidate, partnerId });
          }
        };

        peerConnection.current.onconnectionstatechange = () => {
          console.log("Connection state:", peerConnection.current.connectionState);
        };

        const offer = await peerConnection.current.createOffer();
        await peerConnection.current.setLocalDescription(offer);
        socket.emit("offer", { offer, partnerId });

      } catch (error) {
        console.error("Error setting up WebRTC:", error);
        setStatusMessage("Error accessing camera/microphone. Please check permissions.");
      }
    };

    const joinRoom = () => {
      console.log('Joining room with name:', name, 'and group:', group);
      
      // Add connection status check
      console.log('Socket connected:', socket.connected);
      console.log('Socket ID:', socket.id);
      console.log('Socket server URL:', socketServer);
      
      if (!socket.connected) {
        console.log('Socket not connected, attempting to connect...');
        socket.connect();
        
        // Wait for connection before emitting
        socket.once('connect', () => {
          console.log('Socket connected, now joining room');
          socket.emit("join-room", { name, group });
          setRoomJoined(true);
          
          // Request session info after joining
          setTimeout(() => {
            socket.emit("get-session-info");
          }, 1000);
        });
      } else {
        socket.emit("join-room", { name, group });
        setRoomJoined(true);
        
        // Request session info after joining
        setTimeout(() => {
          socket.emit("get-session-info");
        }, 1000);
      }
    };

    const skipPartner = () => {
      if (pairedUser) {
        socket.emit("skip-partner");
        setPairedUser(null);
        if (peerConnection.current) {
          peerConnection.current.close();
          peerConnection.current = null;
        }
        setStatusMessage("Skipping to next partner...");
      }
    };
  
    return (
      <MainLoggedInLayout>
        {!roomJoined ? (
          name && group ?
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: 400,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              boxShadow: 3,
              bgcolor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h4" gutterBottom>Join Video Chat</Typography>
            <TextField
              label="Your ID"
              variant="outlined"
              value={name}
              fullWidth
              disabled
            />
            <TextField
              label="Group Name"
              variant="outlined"
              value={group}
              fullWidth
              disabled
            />
            <Button variant="contained" color="primary" onClick={joinRoom}>
              Join
            </Button>
          </Box>
          :
          <SpeedMeetingGroupsTable setName={setName} setGroup={setGroup} token={userDetails?.token} user={userDetails} />
        ) : (
          <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
            <Typography variant="h4" gutterBottom>Speed Meeting Session</Typography>
            
            {/* Session Status */}
            <Box sx={{ mb: 3, p: 2, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h6" gutterBottom>Session Status</Typography>
              <Typography>Group: {group}</Typography>
              <Typography>Total Participants: {totalUsers}</Typography>
              {sessionInfo && (
                <>
                  <Typography>Completed Meetings: {sessionInfo.completedMeetings}</Typography>
                  <Typography>Remaining Meetings: {sessionInfo.remainingMeetings}</Typography>
                </>
              )}
              {statusMessage && (
                <Typography sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                  Status: {statusMessage}
                </Typography>
              )}
            </Box>

            {/* Video Chat Area */}
            {pairedUser ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Currently paired with: {pairedUser.name}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: "300px" }}>
                    <Typography variant="subtitle2" gutterBottom>Your Video</Typography>
                    <video 
                      ref={localVideoRef} 
                      autoPlay 
                      muted 
                      style={{ 
                        width: "100%", 
                        maxWidth: "400px",
                        borderRadius: 8, 
                        background: "#000",
                        aspectRatio: "16/9"
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: "300px" }}>
                    <Typography variant="subtitle2" gutterBottom>{pairedUser.name}'s Video</Typography>
                    <video 
                      ref={remoteVideoRef} 
                      autoPlay 
                      style={{ 
                        width: "100%", 
                        maxWidth: "400px",
                        borderRadius: 8, 
                        background: "#000",
                        aspectRatio: "16/9"
                      }}
                    />
                  </Box>
                </Box>
                
                {/* Timer and Controls */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "center" }}>
                  <Typography variant="h6" sx={{ 
                    color: timeLeft < 60 ? "error.main" : "text.primary",
                    fontWeight: "bold" 
                  }}>
                    Time remaining: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={skipPartner}
                    size="small"
                  >
                    Skip Partner
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                p: 4, 
                textAlign: "center", 
                bgcolor: "background.paper", 
                borderRadius: 2, 
                boxShadow: 1 
              }}>
                <Typography variant="h6" gutterBottom>
                  {totalUsers < 2 ? "Waiting for more participants..." : "Finding your next partner..."}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {totalUsers < 2 
                    ? "At least 2 people are needed to start speed meeting." 
                    : "Please wait while we pair you with someone new."
                  }
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </MainLoggedInLayout>
    );
  };
  
  export default SpeedMeeting;